import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, List, Tag, Button, message, Modal, Typography, Statistic, Avatar, Space, Spin, Alert, Progress, Tabs, Input } from 'antd';
import { 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  ClockCircleOutlined, 
  UserOutlined, 
  ProjectOutlined,
  CalendarOutlined,
  ExclamationCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { useAuthStore } from '../store/useAuthStore';
import apiService from '../services/apiService';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { confirm } = Modal;
const { TextArea } = Input;

// TimesheetList component
const TimesheetList = ({ timesheets, loading, actionLoading, onView, onAction, showActions }) => {
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  if (timesheets.length === 0) {
    return (
      <div className="text-center py-12">
        <CheckCircleOutlined className="text-6xl text-green-500 mb-4" />
        <div className="text-xl font-medium text-gray-600 mb-2">No entries found</div>
        <div className="text-gray-500">No timesheet entries match the current filter.</div>
      </div>
    );
  }

  return (
    <List
      dataSource={Array.isArray(timesheets) ? timesheets : []}
      renderItem={(timesheet) => (
        <List.Item
          className="hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
          onClick={() => onView(timesheet)}
          actions={showActions ? [
            <Button
              key="approve"
              type="primary"
              size="small"
              icon={<CheckOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onAction(timesheet._id, 'approved', timesheet);
              }}
              loading={actionLoading}
            >
              Approve
            </Button>,
            <Button
              key="reject"
              danger
              size="small"
              icon={<CloseOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onAction(timesheet._id, 'rejected', timesheet);
              }}
              loading={actionLoading}
            >
              Reject
            </Button>
          ] : []
        }
        >
          <List.Item.Meta
            avatar={<Avatar icon={<UserOutlined />} />}
            title={
              <div className="flex items-center space-x-4">
                <span className="font-medium">{timesheet.employeeName}</span>
                <Tag color="blue">{timesheet.projectName}</Tag>
                <Tag color={timesheet.status === 'approved' ? 'green' : timesheet.status === 'rejected' ? 'red' : 'orange'}>
                  {timesheet.status === 'submitted' ? 'Pending' : timesheet.status.charAt(0).toUpperCase() + timesheet.status.slice(1)}
                </Tag>
              </div>
            }
            description={
              <div>
                <div className="text-sm text-gray-600 mb-1">
                  <span className="font-medium">Hours:</span> {timesheet.hours} | 
                  <span className="font-medium ml-2">Date:</span> {dayjs(timesheet.date).format('MMM DD, YYYY')}
                </div>
                <div className="text-xs text-gray-500">
                  Submitted: {dayjs(timesheet.createdAt).format('MMM DD, YYYY HH:mm')}
                </div>
              </div>
            }
          />
        </List.Item>
      )}
      pagination={{
        defaultPageSize: 8,
        showSizeChanger: true,
        showQuickJumper: true,
        pageSizeOptions: ['8', '16', '24', '50'],
        showTotal: (total) => `Total ${total} entries`,
      }}
    />
  );
};

const ApproveTimesheets = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [allTimesheets, setAllTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  
  const approveTimesheets = async () => {
   const selectedIds = allTimesheets.map(ts => ts._id);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/project-manager/timesheets/status`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              timesheetIds : selectedIds,       // array of timesheet IDs
              status: "approved", // fixed approve
              managerId: user.id, // current logged-in manager
              remarks: "Approved after review",
            }),
          }
        );

        const data = await response.json();
        if (data.success) {
          message.success(data.message);
          fetchAllTimesheets(); // refresh table
        } else {
          message.error(data.message);
        }
        return data; // { success, count, data, message }
      } catch (err) {
        console.error("Approve API error:", err);
        return { success: false, message: "Failed to approve timesheets" };
      }
    };


  useEffect(() => {
    if (user?.id) {
      fetchAllTimesheets();
    }
  }, [user]);

  const fetchAllTimesheets = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/project-manager/dashboard/${user.id}`);
      const data = await response.json();
      
      if (data.success) {
        // Get all timesheets and format them
        const formattedTimesheets = data.data.recentTimesheets
          .map(timesheet => ({
            ...timesheet,
            employeeName: timesheet.employeeId?.name || 'Unknown',
            projectName: timesheet.projectId?.name || 'Unknown'
          }));
        
        setAllTimesheets(formattedTimesheets);
      } else {
        message.error('Failed to fetch timesheets');
      }
    } catch (err) {
      message.error('Failed to fetch timesheets');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTimesheetAction = (timesheetId, status, timesheetData) => {
    const actionText = status === 'approved' ? 'approve' : 'reject';
    let remarks = '';
    
    const modal = Modal.confirm({
      title: `${actionText.charAt(0).toUpperCase() + actionText.slice(1)} Timesheet`,
      icon: <ExclamationCircleOutlined />,
      width: 600,
      content: (
        <div className="mt-4">
          <p>Are you sure you want to {actionText} this timesheet entry?</p>
          <div className="bg-gray-50 p-3 rounded mt-3 mb-4">
            <p><strong>Employee:</strong> {timesheetData.employeeName}</p>
            <p><strong>Project:</strong> {timesheetData.projectName}</p>
            <p><strong>Description:</strong> {timesheetData.description}</p>
            <p><strong>Date:</strong> {dayjs(timesheetData.date).format('MMM DD, YYYY')}</p>
            <p><strong>Hours:</strong> {timesheetData.hours}h</p>
          </div>
          <div>
            <Text strong>Manager Remarks {status === 'rejected' ? '(Required)' : '(Optional)'}:</Text>
            <TextArea
              rows={3}
              placeholder={status === 'approved' ? 'Add any comments for the employee...' : 'Please provide reason for rejection...'}
              onChange={(e) => { remarks = e.target.value; }}
              className="mt-2"
            />
          </div>
        </div>
      ),
      okText: actionText.charAt(0).toUpperCase() + actionText.slice(1),
      okType: status === 'approved' ? 'primary' : 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        if (status === 'rejected' && !remarks.trim()) {
          message.error('Remarks are required when rejecting a timesheet');
          return Promise.reject();
        }
        
        try {
          setActionLoading(true);
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/project-manager/timesheet/${timesheetId}/status`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              status,
              managerId: user.id,
              remarks: remarks.trim()
            })
          });

          const data = await response.json();
          
          if (data.success) {
            message.success(`Timesheet ${status} successfully${remarks.trim() ? ' with remarks' : ''}`);
            fetchAllTimesheets(); // Refresh data
          } else {
            message.error(data.message);
          }
        } catch (err) {
          message.error('Failed to update timesheet status');
          console.error('Action error:', err);
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  const showTimesheetDetail = (timesheet) => {
    setSelectedTimesheet(timesheet);
    setDetailModalVisible(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  // Calculate statistics
  const pendingTimesheets = allTimesheets.filter(ts => ts.status === 'submitted');
  const approvedTimesheets = allTimesheets.filter(ts => ts.status === 'approved');

  const getFilteredTimesheets = () => {
    switch (activeTab) {
      case 'pending':
        return pendingTimesheets;
      case 'approved':
        return approvedTimesheets;
      case 'all':
      default:
        return allTimesheets;
    }
  };

  const filteredTimesheets = getFilteredTimesheets();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  const pendingCount = pendingTimesheets.length;
  const totalHours = filteredTimesheets.reduce((sum, ts) => sum + ts.hours, 0);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <Title level={2}>
          <CheckOutlined className="mr-2 text-green-600" />
          Approve Timesheets
        </Title>
      </div>

      {/* Alert for pending approvals */}
      {pendingTimesheets.length > 0 && (
        <Alert
          message={`${pendingTimesheets.length} timesheet entries are pending your approval`}
          type="warning"
          showIcon
          closable
          className="mb-6"
        />
      )}

      <Row gutter={[24, 24]}>
        {/* Main Content - 70% */}
        <Col xs={24} lg={17}>
          <div className="space-y-6">
            {/* Timesheet Entries with Tabs */}
            <Card className="shadow-sm">
              <Tabs 
                activeKey={activeTab} 
                onChange={setActiveTab}
                items={[
                  {
                    key: 'pending',
                    label: `Pending (${pendingTimesheets.length})`,
                    children: (
                      <TimesheetList 
                        timesheets={pendingTimesheets}
                        loading={loading}
                        actionLoading={actionLoading}
                        onView={showTimesheetDetail}
                        onAction={handleTimesheetAction}
                        showActions={true}
                      />
                    )
                  },
                  {
                    key: 'approved', 
                    label: `Approved (${approvedTimesheets.length})`,
                    children: (
                      <TimesheetList 
                        timesheets={approvedTimesheets}
                        loading={loading}
                        actionLoading={actionLoading}
                        onView={showTimesheetDetail}
                        onAction={handleTimesheetAction}
                        showActions={false}
                      />
                    )
                  },
                  {
                    key: 'all',
                    label: `All Entries (${allTimesheets.length})`,
                    children: (
                      <TimesheetList 
                        timesheets={allTimesheets}
                        loading={loading}
                        actionLoading={actionLoading}
                        onView={showTimesheetDetail}
                        onAction={handleTimesheetAction}
                        showActions={false}
                      />
                    )
                  }
                ]}
              />
            </Card>
          </div>
        </Col>

        {/* Sidebar - 30% */}
        <Col xs={24} lg={7}>
          <div className="space-y-6">
            {/* Summary Statistics */}
            <Card title="Summary" className="shadow-sm">
              <div className="space-y-4">
                <Statistic
                  title="Pending Approvals"
                  value={pendingTimesheets.length}
                  prefix={<ExclamationCircleOutlined className="text-orange-500" />}
                />
                <Statistic
                  title="Approved This Month"
                  value={approvedTimesheets.length}
                  prefix={<CheckCircleOutlined className="text-green-500" />}
                />
                <Statistic
                  title="Total Hours (Current View)"
                  value={totalHours.toFixed(2)}
                  suffix="h"
                  prefix={<ClockCircleOutlined className="text-blue-500" />}
                />
                <Statistic
                  title="Employees Involved"
                  value={[...new Set(filteredTimesheets.map(ts => ts.employeeId?._id).filter(Boolean))].length}
                  prefix={<UserOutlined className="text-green-500" />}
                />
              </div>
            </Card>

            {/* Quick Actions */}
            <Card title="Quick Actions" className="shadow-sm">
              <Space direction="vertical" className="w-full">
                <Button 
                  type="primary" 
                  block 
                  icon={<CheckOutlined />}
                  disabled={pendingTimesheets.length === 0}
                  onClick={approveTimesheets}
                >
                  Bulk Approve All
                </Button>
                <Button 
                  block 
                  icon={<ProjectOutlined />}
                  onClick={() => navigate('/project-manager/projects')}
                >
                  View All Projects
                </Button>
                <Button 
                  block 
                  icon={<UserOutlined />}
                  onClick={() => navigate('/project-manager/team')}
                >
                  Team Overview
                </Button>
              </Space>
            </Card>
          </div>
        </Col>
      </Row>

      {/* Timesheet Detail Modal */}
      <Modal
        title="Timesheet Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>,
          ...(selectedTimesheet?.status === 'submitted' ? [
            <Button
              key="reject"
              danger
              onClick={() => {
                handleTimesheetAction(selectedTimesheet._id, 'rejected', selectedTimesheet);
                setDetailModalVisible(false);
              }}
            >
              Reject
            </Button>,
            <Button
              key="approve"
              type="primary"
              onClick={() => {
                handleTimesheetAction(selectedTimesheet._id, 'approved', selectedTimesheet);
                setDetailModalVisible(false);
              }}
            >
              Approve
            </Button>
          ] : [])
        ]}
        width={600}
      >
        {selectedTimesheet && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Text strong>Employee:</Text>
                <div>{selectedTimesheet.employeeName}</div>
              </div>
              <div>
                <Text strong>Project:</Text>
                <div>{selectedTimesheet.projectName}</div>
              </div>
              <div>
                <Text strong>Date:</Text>
                <div>{dayjs(selectedTimesheet.date).format('MMM DD, YYYY')}</div>
              </div>
              <div>
                <Text strong>Hours:</Text>
                <div>{selectedTimesheet.hours}h</div>
              </div>
              <div>
                <Text strong>Status:</Text>
                <div>
                  <Tag color={selectedTimesheet.status === 'approved' ? 'green' : selectedTimesheet.status === 'rejected' ? 'red' : 'orange'}>
                    {selectedTimesheet.status === 'submitted' ? 'Pending' : selectedTimesheet.status.charAt(0).toUpperCase() + selectedTimesheet.status.slice(1)}
                  </Tag>
                </div>
              </div>
              <div>
                <Text strong>Submitted:</Text>
                <div>{dayjs(selectedTimesheet.createdAt).format('MMM DD, YYYY HH:mm')}</div>
              </div>
              {selectedTimesheet.approvedBy && (
                <div>
                  <Text strong>Approved/Rejected By:</Text>
                  <div>{selectedTimesheet.approvedBy?.name || 'Unknown Manager'}</div>
                </div>
              )}
              {selectedTimesheet.approvedAt && (
                <div>
                  <Text strong>Action Date:</Text>
                  <div>{dayjs(selectedTimesheet.approvedAt).format('MMM DD, YYYY HH:mm')}</div>
                </div>
              )}
            </div>
            <div>
              <Text strong>Work Description:</Text>
              <div className="mt-2 p-3 bg-gray-50 rounded">
                {selectedTimesheet.description || 'No description provided'}
              </div>
            </div>
            {selectedTimesheet.managerRemarks && (
              <div>
                <Text strong>Manager Remarks:</Text>
                <div className="mt-2 p-3 bg-blue-50 border-l-4 border-blue-400 rounded">
                  {selectedTimesheet.managerRemarks}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ApproveTimesheets;
